import { createContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import type { User, AuthTokens, LoginRequest, LoginResponse } from '@shared/types';
import { apiClient } from '@api/client';

export interface LegacyAuthState {
  user: User | null;
  tokens: AuthTokens | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  loginLegacy: (request: LoginRequest) => Promise<void>;
  logout: () => void;
}

export const LegacyAuthContext = createContext<LegacyAuthState | null>(null);

const AUTH_STORAGE_KEY = 'oms-auth';

function parseJwtPayload(token: string): Record<string, unknown> {
  const base64 = token.split('.')[1];
  const json = atob(base64.replace(/-/g, '+').replace(/_/g, '/'));
  return JSON.parse(json);
}

function extractUser(token: string): User {
  const claims = parseJwtPayload(token);
  return {
    id: (claims['Id'] as string) ?? '',
    userName: (claims['sub'] as string) ?? '',
    roleId: (claims['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] as string) ?? '',
    vesselId: (claims['VesselId'] as string) || undefined,
    counterpartyMemberId: (claims['CounterpartyMember'] as string) || undefined,
  };
}

function mapLoginResponse(response: LoginResponse): { user: User; tokens: AuthTokens } {
  const user = extractUser(response.accessToken.token);
  const tokens: AuthTokens = {
    accessToken: response.accessToken.token,
    refreshToken: response.refreshToken,
    expiresAt: Date.now() + response.accessToken.expiresIn * 1000,
  };
  return { user, tokens };
}

function loadStoredAuth(): { user: User; tokens: AuthTokens } | null {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { state?: { user?: User; tokens?: AuthTokens } };
    if (parsed?.state?.user && parsed?.state?.tokens) {
      if (parsed.state.tokens.expiresAt < Date.now()) {
        localStorage.removeItem(AUTH_STORAGE_KEY);
        return null;
      }
      return { user: parsed.state.user, tokens: parsed.state.tokens };
    }
    return null;
  } catch {
    return null;
  }
}

function saveAuth(user: User, tokens: AuthTokens): void {
  localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify({ state: { user, tokens } }));
}

function clearAuth(): void {
  localStorage.removeItem(AUTH_STORAGE_KEY);
}

export function LegacyAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [tokens, setTokens] = useState<AuthTokens | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const stored = loadStoredAuth();
    if (stored) {
      setUser(stored.user);
      setTokens(stored.tokens);
    }
    setIsLoading(false);
  }, []);

  const loginLegacy = useCallback(async (request: LoginRequest) => {
    const response = await apiClient.post<LoginResponse>('/v1/auth/login', request);
    const { user, tokens } = mapLoginResponse(response);
    setUser(user);
    setTokens(tokens);
    saveAuth(user, tokens);
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    setTokens(null);
    clearAuth();
  }, []);

  return (
    <LegacyAuthContext.Provider
      value={{
        user,
        tokens,
        isAuthenticated: !!user,
        isLoading,
        loginLegacy,
        logout,
      }}
    >
      {children}
    </LegacyAuthContext.Provider>
  );
}
